
import { useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { Profile, fetchProfile, clearProfileCache } from '@/services/profiles';
import { createLogger } from '@/services/utils/loggingUtils';
import { toast } from 'sonner';

const logger = createLogger('useProfileLoading');

export const useProfileLoading = (refreshSession: () => Promise<boolean>) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const profileFetchInProgress = useRef(false);
  const profileRetryCount = useRef(0);
  const profileFetchTimeout = useRef<number | null>(null);

  const loadUserProfile = useCallback(async (userId: string) => {
    if (profileFetchInProgress.current) {
      return;
    }
    
    try {
      profileFetchInProgress.current = true;
      logger.info('Loading profile for user:', userId);
      
      // Clear any existing timeout
      if (profileFetchTimeout.current) {
        clearTimeout(profileFetchTimeout.current);
        profileFetchTimeout.current = null;
      }
      
      // Clear profile cache if we've had multiple failures
      if (profileRetryCount.current > 1) {
        clearProfileCache(userId);
      }
      
      const profileData = await fetchProfile(userId);
      
      if (profileData) {
        logger.info('Profile loaded successfully:', {
          userId,
          username: profileData.username || profileData.email,
          isAdmin: profileData.is_admin
        });
        setProfile(profileData);
        profileRetryCount.current = 0;
      } else {
        logger.warn('No profile data returned for user:', userId);
        
        // Increment retry count and schedule retry
        profileRetryCount.current += 1;
        
        if (profileRetryCount.current <= 3) {
          logger.info(`Scheduling profile retry attempt ${profileRetryCount.current} in ${profileRetryCount.current * 2}s`);
          
          profileFetchTimeout.current = window.setTimeout(() => {
            logger.info(`Executing scheduled profile retry attempt ${profileRetryCount.current}`);
            profileFetchInProgress.current = false;
            loadUserProfile(userId);
          }, profileRetryCount.current * 2000); // Exponential backoff
        } else {
          // After 3 retries, force session refresh and try one more time
          logger.warn(`Profile load failed after ${profileRetryCount.current} attempts, refreshing session`);
          await refreshSession();
          
          try {
            const retryProfileData = await fetchProfile(userId);
            if (retryProfileData) {
              logger.info('Profile loaded successfully after session refresh');
              setProfile(retryProfileData);
            } else {
              logger.error('Failed to load profile even after session refresh');
              
              // Last resort - create an emergency minimal profile for the user
              // This helps prevent UI from getting stuck in loading states
              const minimalProfile: Profile = {
                id: userId,
                username: 'User', // Minimal fallback name
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                credit_balance: 100,
                is_admin: false,
                email_notifications: true,
                notify_swap_offers: true,
                notify_swap_responses: true,
                notify_messages: true,
                daily_digest_email: true,
                weekly_digest_email: false,
                push_notifications: false,
                push_swap_offers: false,
                push_swap_responses: false,
                push_messages: false,
                push_weekly_digest: false,
                is_deleted: false,
                display_username: null,
                lab: null,
                university: null,
                location: null,
                city: null,
                country: null,
                research_interests: null,
                expertise: null,
                linkedin_url: null,
                website_url: null,
                email: null
              };
              
              logger.info('Setting emergency minimal profile after all load attempts failed');
              setProfile(minimalProfile);
              
              toast.error('Unable to fully load your profile data. Some features may be limited.');
            }
          } catch (finalError) {
            logger.error('Fatal error loading profile after all attempts:', finalError);
          } finally {
            profileFetchInProgress.current = false;
          }
        }
      }
    } catch (error) {
      logger.error('Failed to load profile:', error);
      profileFetchInProgress.current = false;
    } finally {
      profileFetchInProgress.current = false;
    }
  }, [refreshSession]);

  const resetProfileState = useCallback(() => {
    profileRetryCount.current = 0;
    if (profileFetchTimeout.current) {
      clearTimeout(profileFetchTimeout.current);
      profileFetchTimeout.current = null;
    }
    profileFetchInProgress.current = false;
  }, []);

  return { 
    profile, 
    setProfile, 
    loadUserProfile,
    resetProfileState
  };
};
